// src/components/gestionEvento/PredefinedPruebasModal.jsx
import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog, Box, IconButton, Typography, InputBase,
  Chip, List, ListItem, ListItemAvatar, Avatar, ListItemText,
  ListItemSecondaryAction, Button, Divider, useTheme, useMediaQuery, alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import TimerIcon from '@mui/icons-material/Timer';
import HeightIcon from '@mui/icons-material/Height';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import SportsIcon from '@mui/icons-material/Sports';

import PropTypes from 'prop-types';
import predefinedPruebas from '../../../../constants/predefinedPruebas';

function iconFor(testType) {
  const t = (testType || '').toLowerCase();
  if (t.includes('carrera') || t.includes('velocidad') || t.includes('vallas')) return <TimerIcon />;
  if (t.includes('salto')) return <HeightIcon />;
  if (t.includes('lanz')) return <ChangeCircleIcon />;
  return <SportsIcon />;
}

function prettyType(testType) {
  const t = (testType || '').toLowerCase();
  if (t.includes('carrera')) return 'Carrera';
  if (t.includes('salto')) return 'Salto';
  if (t.includes('lanz')) return 'Lanzamiento';
  return testType || 'Otro';
}

export default function PredefinedPruebasModal({
  open,
  handleClose,
  handleAddSelectedPruebas,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (open) {
      setSearch('');
      setActiveFilter('Todos');
      setSelected(new Set());
    }
  }, [open]);

  const filters = useMemo(() => {
    const set = new Set(predefinedPruebas.map(p => prettyType(p.testType)));
    return ['Todos', ...Array.from(set)];
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return predefinedPruebas.filter(p => {
      const byType = activeFilter === 'Todos' || prettyType(p.testType) === activeFilter;
      if (!q) return byType;
      const inName = p.nombre?.toLowerCase().includes(q);
      const inType = p.testType?.toLowerCase().includes(q);
      return byType && (inName || inType);
    });
  }, [search, activeFilter]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const onConfirm = () => {
    const pruebasToAdd = predefinedPruebas
      .filter(p => selected.has(p.id))
      .map(p => ({
        id: p.id,
        nombre: p.nombre,
        testType: p.testType,
        eventCode: p.eventCode,
        tipoMarca: p.tipoMarca,
        descripcion: p.descripcion || '',
      }));
    handleAddSelectedPruebas(pruebasToAdd);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}         // full-screen solo en móvil
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: isMobile
          ? { m: 0, height: '100dvh', maxHeight: '100dvh', width: '100%', maxWidth: '100%', borderRadius: 0, display: 'flex', flexDirection: 'column' }
          : { borderRadius: 2, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header sticky con X */}
      <Box
        sx={(t) => ({
          position: 'sticky',
          top: 0,
          zIndex: 3,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${t.palette.divider}`,
          bgcolor: t.palette.background.paper,
        })}
      >
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Añadir Pruebas
        </Typography>
        <IconButton onClick={handleClose} aria-label="cerrar">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Contenido */}
      <Box sx={{ p: 2, pb: 10 }}>
        {/* Buscador pill */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 1,
            mb: 1.5,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
          }}
        >
          <SearchIcon sx={{ opacity: 0.6 }} />
          <InputBase
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar pruebas"
            inputProps={{ 'aria-label': 'buscar pruebas' }}
            sx={{ flex: 1, fontSize: '1rem' }}
          />
        </Box>

        {/* Chips — una sola línea scrollable y color activo */}
        <Box
          sx={(t) => ({
            position: 'sticky',
            top: isMobile ? 64 : 0, // pequeño offset cuando es full-screen
            zIndex: 2,
            mb: 2,
            pb: 1,
            bgcolor: t.palette.background.paper,
          })}
        >
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 1,
              py: 0.5,
              '::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {filters.map(f => (
              <Chip
                key={f}
                label={f}
                onClick={() => setActiveFilter(f)}
                clickable
                sx={(t)=>({
                  borderRadius: 999,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  ...(activeFilter === f
                    ? { bgcolor: t.palette.primary.main, color: t.palette.primary.contrastText }
                    : { bgcolor: alpha(t.palette.text.primary, 0.08) }),
                })}
              />
            ))}
          </Box>
        </Box>

        {/* Lista de pruebas */}
        <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
          Pruebas predefinidas
        </Typography>

        <List dense disablePadding sx={{ mt: 1 }}>
          {filtered.map((p, idx) => {
            const isSel = selected.has(p.id);
            return (
              <React.Fragment key={p.id}>
                <ListItem
                  sx={(t)=>({
                    borderRadius: 3,
                    px: 1.25,
                    py: 1.25,
                    mb: 1.25,
                    bgcolor: alpha(t.palette.common.black, 0.02),
                    border: `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
                  })}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                      {iconFor(p.testType)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                        {p.nombre}
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ color: 'text.secondary', mt: 0.25 }}>
                        {prettyType(p.testType)}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => toggle(p.id)}
                      aria-label={isSel ? 'deseleccionar' : 'seleccionar'}
                      sx={(t)=>({
                        width: 40, height: 40,
                        borderRadius: '999px',
                        bgcolor: isSel ? alpha(t.palette.primary.main, 0.12) : alpha(t.palette.text.primary, 0.06),
                        border: `1px solid ${alpha(t.palette.text.primary, 0.15)}`,
                      })}
                    >
                      {isSel ? <CheckIcon color="primary" /> : <AddIcon />}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {idx < filtered.length - 1 && <Divider sx={{ opacity: 0.15 }} />}
              </React.Fragment>
            );
          })}
          {filtered.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>No hay pruebas que coincidan con tu búsqueda.</Typography>
            </Box>
          )}
        </List>
      </Box>

      {/* Footer sticky — mismos botones que el de categorías */}
      <Box
        sx={(t) => ({
          position: 'sticky',
          bottom: 0,
          zIndex: 3,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',   // juntos a la derecha
          gap: 2,
          borderTop: `1px solid ${t.palette.divider}`,
          bgcolor: t.palette.background.paper,
        })}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={(t) => ({
            borderRadius: 999,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontWeight: 700,
            color: t.palette.secondary.main,
            border: `1px solid ${t.palette.secondary.main}`,
            '&:hover': { bgcolor: t.palette.action.hover },
          })}
        >
          Cancelar
        </Button>

        <Button
          onClick={onConfirm}
          disabled={selected.size === 0}
          variant="contained"
          sx={{
            borderRadius: 999,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          {selected.size > 0
            ? `Añadir ${selected.size} prueba${selected.size > 1 ? 's' : ''}`
            : 'Añadir Pruebas'}
        </Button>
      </Box>
    </Dialog>
  );
}

PredefinedPruebasModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAddSelectedPruebas: PropTypes.func.isRequired,
};
